import { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Drawer from "@mui/material/Drawer";
import { Theme } from "../data/style/Theme";
import Lifts from "../components/Display/Lifts";
import { useAuth } from "../context/AuthContext";
import Cardio from "../components/Display/Cardio";
import useScreenSize from "../context/ScreenContext";
import { CardTitle } from "../components/theme/texts";
import Gymnastics from "../components/Display/Gymnastics";
import SelectInput from "../components/Inputs/SelectInput";
import { PS_CHARTS_TOOLTIP_TEXTS } from "../data/assets/texts";
import ExerciseChart from "../components/charts/ExerciseChart";
import ExerciseStats from "../components/Display/ExerciseStats";
import ResultBarChart from "../components/Display/ResultBarChart";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PerformanceBarChart from "../components/Display/PerformanceBarChart";
import {
  scrollBar,
  SpacedRow,
  AllCenteredColumn,
  MainText,
  SmallTitle,
} from "../data/style/Style";
import RequestButton from "../components/buttons/RequestButton";

const PanelRow = ({ children, width }) => {
  return (
    <Stack
      direction={"row"}
      justifyContent={width > 1150 && "space-between"}
      gap={2}
      flexWrap={"wrap"}
      rowGap={2}
    >
      {children}
    </Stack>
  );
};
const Performance = () => {
  const { settings } = useAuth();
  const [exerciseId, setExerciseId] = useState(0);
  const [dateRangeId, setDateRangeId] = useState(1);
  const [exerciseType, setExerciseType] = useState(0);
  const [collapseActive, setCollapseActive] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(0);
  const { size, mobileDevice } = useScreenSize();
  const [sessions, setSessions] = useState(0);
  var [width] = size;

  let showExerciseDetails = (exercise, type) => {
    if (exercise.id === exerciseId) {
      setCollapseActive(false);
      setExerciseId(0);
      return;
    }
    setCollapseActive(true);
    setSelectedExercise(exercise);
    setExerciseId(exercise.id);
    setExerciseType(type);
  };

  const DATERANGEOPTIONS = [
    {
      id: 1,
      name: "Last month",
    },
    {
      id: 2,
      name: "Last 3 months",
    },
    {
      id: 3,
      name: "Last 6 months",
    },
    { id: 4, name: "Last year" },
    { id: 5, name: "Overall" },
  ];

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setCollapseActive(open);
  };

  const weight_unit = settings.weight === 0 ? " kg" : " lb";

  const color =
    exerciseType === "cardio"
      ? Theme.palette.up
      : exerciseType === "gymnastics"
        ? Theme.palette.blue
        : Theme.palette.gold;

  return (
    <Stack height={"100%"} overflow={"auto"} sx={scrollBar} gap={3} pr={1}>
      <Stack gap={1}>
        <SpacedRow
          width={mobileDevice ? "94%" : "99%"}
          mb={1}
          alignItems={"end"}
        >
          <CardTitle
            mainTitle={"Results"}
            subTitle={"Workout stats"}
            isMobile={mobileDevice}
          />
          <Box sx={{ width: "15%", minWidth: mobileDevice ? "80px" : "120px" }}>
            <SelectInput
              options={DATERANGEOPTIONS}
              value={dateRangeId}
              setValue={setDateRangeId}
              color={Theme.palette.muted}
              label={"Period"}
            />
          </Box>
        </SpacedRow>
        <PanelRow width={width}>
          <ResultBarChart
            type={1}
            dateRangeId={dateRangeId}
            label={"For time"}
          />
          <ResultBarChart type={2} dateRangeId={dateRangeId} label={"AMRAP"} />
          <ResultBarChart type={3} dateRangeId={dateRangeId} label={"EMOM"} />
          <ResultBarChart
            type={4}
            dateRangeId={dateRangeId}
            label={"Interval"}
          />
          <ResultBarChart type={5} dateRangeId={dateRangeId} label={"Ladder"} />
        </PanelRow>
      </Stack>
      <Stack gap={1}>
        <CardTitle mainTitle={"Performance Stats"} subTitle={"Analytics"} />
        <PanelRow width={width}>
          <PerformanceBarChart
            title={"Margin from timecaps"}
            subTitle={"Average minutes above/below timecap"}
            tooltip={PS_CHARTS_TOOLTIP_TEXTS.fortime}
            dateRangeId={dateRangeId}
            type={1}
          />
          <PerformanceBarChart
            title={"Margin from projected scores"}
            subTitle={"Average reps above/below projected score"}
            tooltip={PS_CHARTS_TOOLTIP_TEXTS.amrap}
            dateRangeId={dateRangeId}
            type={2}
          />
          <PerformanceBarChart
            tooltip={PS_CHARTS_TOOLTIP_TEXTS.intervals}
            title={"Margin from interval"}
            subTitle={"Average minutes above/below interval"}
            dateRangeId={dateRangeId}
            type={4}
          />
          <PerformanceBarChart
            tooltip={PS_CHARTS_TOOLTIP_TEXTS.amrap}
            title={"Margin from projected scores (Ladder)"}
            subTitle={"Average reps above/below projected score"}
            dateRangeId={dateRangeId}
            type={5}
          />
        </PanelRow>
      </Stack>
      <Stack gap={1} pb={2}>
        <CardTitle mainTitle={"Personal Bests"} subTitle={"Records"} />
        <PanelRow width={width}>
          <Cardio showExerciseDetails={showExerciseDetails} />
          <Lifts showExerciseDetails={showExerciseDetails} />
          <Gymnastics showExerciseDetails={showExerciseDetails} />
        </PanelRow>
      </Stack>
      <Drawer
        anchor={"right"}
        open={collapseActive}
        onClose={toggleDrawer(false)}
        transitionDuration={{ enter: 500, exit: 500 }}
      >
        <Stack
          p={1}
          gap={1}
          role="presentation"
          height={"98vh"}
          overflow={"auto"}
          sx={{ ...scrollBar, background: Theme.palette.surface }}
          onKeyDown={toggleDrawer(false)}
        >
          <SpacedRow alignItems={"center"}>
            <CardTitle
              subTitle={"Exercise Progress"}
              mainTitle={
                <>
                  {exerciseType === "cardio" && (
                    <>
                      {settings.distance === 0 ? (
                        <>
                          {selectedExercise.distance > 1000
                            ? selectedExercise.distance / 1000 + " km"
                            : selectedExercise.distance + " m"}
                        </>
                      ) : (
                        <>
                          {selectedExercise.distance > 1000
                            ? selectedExercise.distance_ft + " mi"
                            : selectedExercise.distance_ft + " ft"}
                        </>
                      )}
                      &nbsp;
                    </>
                  )}
                  {selectedExercise.exercise_name}
                </>
              }
            />
            <Box>
              <RequestButton
                alt={true}
                small={true}
                onClick={toggleDrawer(false)}
                text={<ArrowForwardIcon />}
              />
            </Box>
          </SpacedRow>
          <SpacedRow gap={2}>
            <SpacedRow
              p={1}
              sx={{
                border: 1,
                borderRadius: 4,
                background: color + "07",
                borderColor: color,
              }}
              alignItems={"center"}
              width={"70%"}
            >
              <Stack gap={1}>
                <SmallTitle
                  sx={{
                    fontSize: "0.6rem",
                    color: color + "88 !important",
                  }}
                >
                  Personal Best
                </SmallTitle>
                <Stack gap={1} direction={"row"} alignItems={"end"}>
                  <MainText
                    fontWeight={700}
                    sx={{
                      color: color + " !important",
                    }}
                  >
                    {exerciseType === "cardio" ? (
                      selectedExercise.converted_score
                    ) : exerciseType === "weightlifting" ? (
                      <>
                        {settings.weight === 0
                          ? selectedExercise.value
                          : selectedExercise.weight_lb}
                      </>
                    ) : (
                      selectedExercise.value
                    )}
                  </MainText>
                  <SmallTitle
                    sx={{
                      fontSize: "0.6rem",
                      color: color + "88 !important",
                    }}
                  >
                    {exerciseType === "cardio"
                      ? " min"
                      : exerciseType === "gymnastics"
                        ? "unbroken reps"
                        : weight_unit}
                  </SmallTitle>
                </Stack>
              </Stack>
              <MainText fontSize={"1.5rem"}>🏆</MainText>
            </SpacedRow>
            <AllCenteredColumn
              sx={{
                border: 1,
                borderRadius: 4,
                borderColor: Theme.palette.border,
                background: Theme.palette.card,
              }}
              width={"25%"}
            >
              <MainText
                fontSize={"1.2rem"}
                fontWeight={700}
                sx={{ color: Theme.palette.orange + " !important" }}
              >
                {sessions}
              </MainText>
              <SmallTitle fontSize={"0.6rem"}>
                {sessions > 1 ? "sessions" : "session"}
              </SmallTitle>
            </AllCenteredColumn>
          </SpacedRow>
          <Stack gap={2} p={1} alignItems={"center"}>
            <ExerciseChart
              selectedExercise={selectedExercise}
              exerciseType={exerciseType}
              setSessions={setSessions}
            />
            <ExerciseStats
              selectedExercise={selectedExercise}
              exerciseType={exerciseType}
            />
          </Stack>
        </Stack>
      </Drawer>
    </Stack>
  );
};

export default Performance;
